/** This module retains no authorization code; the Portal caller owns the deadline. */
import { abortError } from './feishuRecovery';
type AuthResult = { code?: unknown };
type AuthFailure = { errno?: unknown };
type AuthOptions = {
  appID?: string;
  appId?: string;
  scopeList?: string[];
  success: (result: AuthResult) => void;
  fail: (error: AuthFailure) => void;
};
type FeishuClient = {
  requestAccess?: (options: AuthOptions) => void;
  requestAuthCode?: (options: AuthOptions) => void;
};
type FeishuSdk = { ready?: (callback: () => void) => void };

const SDK_URL = 'https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.48.js';
let sdkLoad: Promise<void> | undefined;

export class FeishuBridgeUnavailable extends Error {
  constructor() { super('飞书客户端能力不可用'); }
}

function bridge() {
  return window as typeof window & { h5sdk?: FeishuSdk; tt?: FeishuClient };
}

function loadSdk() {
  if (sdkLoad) return sdkLoad;
  sdkLoad = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new FeishuBridgeUnavailable());
    };
    document.head.appendChild(script);
  }).catch(error => {
    sdkLoad = undefined;
    throw error;
  });
  return sdkLoad;
}

function waitForSdk(signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    loadSdk().then(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, error => {
      signal?.removeEventListener('abort', onAbort);
      reject(error);
    });
  });
}

export async function acquireFeishuCode(appId: string, signal?: AbortSignal): Promise<string> {
  if (signal?.aborted) throw abortError();
  const initial = bridge();
  if (!initial.h5sdk || !initial.tt || typeof initial.h5sdk.ready !== 'function'
      || typeof initial.tt.requestAccess !== 'function'
      && typeof initial.tt.requestAuthCode !== 'function') {
    await waitForSdk(signal);
  }
  if (signal?.aborted) throw abortError();
  // A matching UA or a downloaded script alone never authorizes a JSAPI call.
  if (!bridge().h5sdk || !bridge().tt) throw new FeishuBridgeUnavailable();
  return new Promise<string>((resolve, reject) => {
    let finished = false;
    const onAbort = () => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    const finish = (value: unknown) => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener('abort', onAbort);
      if (typeof value === 'string' && value.length > 0 && value.length <= 4096) resolve(value);
      else reject(new Error('飞书授权失败'));
    };
    const sdk = bridge().h5sdk;
    if (typeof sdk?.ready !== 'function') {
      finished = true;
      signal?.removeEventListener('abort', onAbort);
      reject(new FeishuBridgeUnavailable());
      return;
    }
    const legacy = () => {
      const client = bridge().tt;
      if (typeof client?.requestAuthCode !== 'function') return finish(undefined);
      try {
        client.requestAuthCode({ appId, success: result => finish(result.code), fail: () => finish(undefined) });
      } catch { finish(undefined); }
    };
    try {
      sdk.ready(() => {
        if (finished) return;
        const client = bridge().tt;
        if (!client || typeof client.requestAccess !== 'function'
            && typeof client.requestAuthCode !== 'function') {
          finished = true;
          signal?.removeEventListener('abort', onAbort);
          reject(new FeishuBridgeUnavailable());
          return;
        }
        if (typeof client.requestAccess === 'function') {
          try {
            client.requestAccess({
              appID: appId,
              scopeList: [],
              success: result => finish(result.code),
              fail: error => {
                if (error.errno === 103) legacy();
                else finish(undefined);
              },
            });
          } catch { finish(undefined); }
        } else legacy();
      });
    } catch { finish(undefined); }
  });
}
